import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "../lib/api";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };
const list = (xs, n = 2) => (xs || []).length <= n ? (xs || []).join(", ") : `${xs.slice(0, n).join(", ")} +${xs.length - n}`;

/** Returned material pending receipt — one row per product per job, whatever rooms it came from.
 *  The receiver types the count actually on the dock; a short count becomes a "needs locating" item. */
export default function WarehouseReturnsQueue({ actor, canPull, onChanged }) {
  const [data, setData] = useState({ groups: [], unlocated: [], in_transit_count: 0 });
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);   // { g, qty }
  const [extra, setExtra] = useState({});         // key -> { damaged, damaged_note, shelf }
  const load = async () => {
    try { setData((await api.get("/warehouse/returns/grouped")).data); } catch { setData({ groups: [], unlocated: [], in_transit_count: 0 }); }
  };
  useEffect(() => { load(); }, []);

  const receive = async (g, qty) => {
    setBusy(g.key);
    const x = extra[g.key] || {};
    try {
      const { data: d } = await api.post("/warehouse/returns/receive", {
        return_ids: g.return_ids, received_qty: qty, by: actor,
        damaged_qty: Number(x.damaged || 0), damaged_note: (x.damaged_note || "").trim(),
        shelf_location: (x.shelf ?? g.shelf_location ?? "").trim(),
      });
      toast[d.unlocated > 0 ? "warning" : "success"](d.message);
      setCounts((c) => { const n = { ...c }; delete n[g.key]; return n; });
      setExtra((c) => { const n = { ...c }; delete n[g.key]; return n; });
      setConfirm(null); load(); onChanged?.();
    } catch (e) { toast.error(errText(e, "Could not check the return in")); } finally { setBusy(""); }
  };
  const onReceive = (g) => {
    const raw = counts[g.key];
    const qty = raw === undefined || raw === "" ? g.quantity : Number(raw);
    if (!Number.isFinite(qty) || qty < 0) { toast.info("Enter the count actually received"); return; }
    const x = extra[g.key] || {};
    const dmg = Number(x.damaged || 0);
    if (dmg > qty) { toast.info("Damaged units cannot be more than what came back"); return; }
    if (dmg > 0 && !(x.damaged_note || "").trim()) { toast.info("Say what is wrong with the damaged units"); return; }
    if (qty !== g.quantity) { setConfirm({ g, qty }); return; }
    receive(g, qty);
  };
  const setX = (key, patch) => setExtra((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));
  const locate = async (u, outcome) => {
    setBusy(u.id);
    try {
      const { data: d } = await api.post(`/warehouse/returns/${u.id}/locate`, { outcome, by: actor });
      toast.success(d.message); load(); onChanged?.();
    } catch (e) { toast.error(errText(e, "Could not close that out")); } finally { setBusy(""); }
  };

  const total = data.groups.length + data.unlocated.length;
  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="warehouse-returns-queue">
      <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Returns to check in</span>
        <span className="text-[11px] text-muted-foreground hidden sm:inline">like items grouped per job — count what is on the dock; it goes back on hand when you confirm</span>
        <span className="ml-auto text-xs font-bold tabular-nums" data-testid="warehouse-returns-count">{data.groups.length}</span>
        {data.unlocated.length > 0 && <span className="text-xs font-bold text-red-700 tabular-nums" data-testid="warehouse-unlocated-count">{data.unlocated.length} needs locating</span>}
        <button className="text-[11px] underline text-muted-foreground" data-testid="warehouse-returns-toggle" onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Show"}</button>
      </div>
      {!open ? null : total === 0 ? (
        <div className="p-3 text-center text-xs text-muted-foreground/70" data-testid="warehouse-returns-empty">Nothing in transit.</div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {data.groups.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-muted text-muted-foreground uppercase text-[10px]">
                <tr><th className="text-left p-2">Job</th><th className="text-left p-2">Product</th><th className="text-left p-2">Coming from</th>
                  <th className="text-right p-2">Expected</th><th className="text-right p-2">Received</th><th className="p-2"></th></tr>
              </thead>
              <tbody>
                {data.groups.map((g) => (
                  <tr key={g.key} className="border-t border-border align-top" data-testid={`warehouse-return-group-${g.product_code}`}>
                    <td className="p-2"><div className="font-mono text-[11px]">{g.job_number || ""}</div>{g.project_name || "—"}</td>
                    <td className="p-2">
                      <div className="font-mono font-semibold" data-testid={`warehouse-return-product-${g.product_code}`}>{g.product_code}</div>
                      {(g.kinds || []).includes("not_delivered") && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-amber-100 text-amber-800 border border-amber-200"
                              data-testid={`warehouse-return-kind-${g.product_code}`}>not delivered — back from site</span>
                      )}
                      {g.description && g.description !== g.product_code && <div className="text-muted-foreground truncate max-w-[260px]" title={g.description}>{g.description}</div>}
                      <div className="text-[10px] text-muted-foreground">{list(g.scope_types)}{g.reasons.length ? ` · ${g.reasons.map((r) => r.replace("_", " ")).join(", ")}` : ""}</div>
                    </td>
                    <td className="p-2 text-[11px] text-muted-foreground" data-testid={`warehouse-return-from-${g.product_code}`}>
                      <div>{g.count} return{g.count === 1 ? "" : "s"} · {list(g.tickets)}{g.methods.length ? ` · ${g.methods.join("/")}` : ""}</div>
                      <div className="truncate max-w-[240px]" title={g.locations.join("\n")}>{list(g.locations, 2)}</div>
                      <div>sent by {list(g.senders)}</div>
                    </td>
                    <td className="p-2 text-right tabular-nums font-semibold" data-testid={`warehouse-return-expected-${g.product_code}`}>{g.quantity}</td>
                    <td className="p-2 text-right">
                      {canPull && (<div className="flex flex-col items-end gap-1">
                        <Input type="number" min="0" step="any" value={counts[g.key] ?? g.quantity} className="h-8 w-20 text-right inline-block"
                               data-testid={`warehouse-return-count-${g.product_code}`}
                               onChange={(e) => setCounts((c) => ({ ...c, [g.key]: e.target.value }))} />
                        <Input type="number" min="0" step="any" placeholder="damaged" value={extra[g.key]?.damaged ?? ""} className="h-7 w-20 text-right text-[11px]"
                               data-testid={`warehouse-return-damaged-${g.product_code}`}
                               onChange={(e) => setX(g.key, { damaged: e.target.value })} />
                        {Number(extra[g.key]?.damaged || 0) > 0 && (
                          <Input placeholder="what is wrong?" value={extra[g.key]?.damaged_note ?? ""} className="h-7 w-32 text-[11px]"
                                 data-testid={`warehouse-return-damaged-note-${g.product_code}`}
                                 onChange={(e) => setX(g.key, { damaged_note: e.target.value })} />
                        )}
                        <Input placeholder="shelf (optional)" value={extra[g.key]?.shelf ?? g.shelf_location ?? ""} className="h-7 w-32 text-[11px]"
                               data-testid={`warehouse-return-shelf-${g.product_code}`}
                               onChange={(e) => setX(g.key, { shelf: e.target.value })} />
                      </div>)}
                    </td>
                    <td className="p-2 text-right">
                      {canPull ? (
                        <Button size="sm" className="h-8" disabled={busy === g.key} onClick={() => onReceive(g)} data-testid={`warehouse-confirm-return-${g.product_code}`}>Confirm receipt</Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/70" data-testid={`warehouse-confirm-locked-${g.product_code}`}>switch to the Warehouse or Admin role to confirm</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data.unlocated.length > 0 && (
            <div className="border-t border-red-200 bg-red-50/40" data-testid="warehouse-unlocated-list">
              <div className="px-3 py-1.5 text-[10px] uppercase font-semibold text-red-800">Needs locating — left the site, never reached the shop</div>
              <table className="w-full text-xs">
                <tbody>
                  {data.unlocated.map((u) => (
                    <tr key={u.id} className="border-t border-red-100" data-testid={`warehouse-unlocated-${u.id}`}>
                      <td className="p-2">{u.project_name || "—"}</td>
                      <td className="p-2 font-mono font-semibold">{u.product_code}</td>
                      <td className="p-2 text-[11px] text-muted-foreground">{u.ticket_number || ""} · {u.note} · sent by {u.created_by} · counted short by {u.confirmed_by}</td>
                      <td className="p-2 text-right tabular-nums font-semibold text-red-700" data-testid={`warehouse-unlocated-qty-${u.id}`}>{u.unlocated_qty}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        {canPull && (<>
                          <Button size="sm" variant="outline" className="h-8 mr-1" disabled={busy === u.id} onClick={() => locate(u, "found")} data-testid={`warehouse-unlocated-found-${u.id}`}>Found — put on hand</Button>
                          <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-300" disabled={busy === u.id}
                                  onClick={() => { if (window.confirm(`Write off ${u.unlocated_qty} ${u.product_code} as lost?`)) locate(u, "lost"); }} data-testid={`warehouse-unlocated-lost-${u.id}`}>Write off</Button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {confirm && (
        <Dialog open onOpenChange={(v) => { if (!v) setConfirm(null); }}>
          <DialogContent className="max-w-md" data-testid="warehouse-return-short-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">{confirm.qty < confirm.g.quantity ? "Short count" : "More than expected"} — {confirm.g.product_code}</DialogTitle>
              <DialogDescription className="text-xs">
                {confirm.qty < confirm.g.quantity
                  ? <>Expected <b>{confirm.g.quantity}</b>, counted <b>{confirm.qty}</b>. The <b>{confirm.g.quantity - confirm.qty}</b> missing unit(s) left the site but never reached the shop — they will be flagged <b>needs locating</b> and the PM, foreman and sender notified.</>
                  : <>Expected <b>{confirm.g.quantity}</b>, counted <b>{confirm.qty}</b>. All <b>{confirm.qty}</b> go back on hand; the extra <b>{confirm.qty - confirm.g.quantity}</b> is noted as an over-receipt.</>}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)} data-testid="warehouse-return-short-cancel">Recount</Button>
              <Button onClick={() => receive(confirm.g, confirm.qty)} disabled={!!busy} data-testid="warehouse-return-short-confirm">Confirm {confirm.qty} received</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
